import { ChartEvolutionPeriodData, EvolutionPeriodData } from '@/api';
import { TIMESPAN_TO_PERIOD_KEY } from '@/constants/timespan';
import { TIMESPAN } from '@/enums/timespan';

interface PerformanceFilterProps {
  lineChartTimespan: TIMESPAN;
  setPerformance: (value: number | null) => void;
  setPlusMoinsValue: (value: number | null) => void;
}

type PerformanceFilterFunction = (props: PerformanceFilterProps) => void;

interface UsePerformanceFilterReturn {
  filterPerformanceDashboard: PerformanceFilterFunction;
  filterPerformanceInvestment: PerformanceFilterFunction;
}

interface PerformanceFilterData {
  evolution?: { performance?: number | null; plusMoinsValue?: number | null } | null;
  evolutionPeriode?: ChartEvolutionPeriodData | EvolutionPeriodData[] | null;
}

export const performanceFilter = (data: PerformanceFilterData | undefined): UsePerformanceFilterReturn => {
  const filterPerformanceDashboard = ({
    lineChartTimespan,
    setPerformance,
    setPlusMoinsValue,
  }: PerformanceFilterProps) => {
    if (!data) return;

    // Current API format: evolutionPeriode is an array of { key, performance, plusMoinsValue }
    if (Array.isArray(data.evolutionPeriode)) {
      const periodKey = TIMESPAN_TO_PERIOD_KEY[lineChartTimespan];
      const periodEntry = data.evolutionPeriode.find((entry) => entry.key === periodKey);
      setPerformance(periodEntry?.performance ?? null);
      setPlusMoinsValue(periodEntry?.plusMoinsValue ?? null);
      return;
    }

    // Legacy format: evolutionPeriode is an object (performanceYTD, plusMoinsValue1M...)
    if (data.evolutionPeriode) {
      switch (lineChartTimespan) {
        case TIMESPAN.allTime:
          setPerformance(data.evolution?.performance ?? null);
          setPlusMoinsValue(data.evolution?.plusMoinsValue ?? null);
          break;
        case TIMESPAN.year:
          setPerformance(data.evolutionPeriode.performanceYTD ?? null);
          setPlusMoinsValue(data.evolutionPeriode.plusMoinsValueYTD ?? null);
          break;
        case TIMESPAN.month:
          setPerformance(data.evolutionPeriode.performance1M ?? null);
          setPlusMoinsValue(data.evolutionPeriode.plusMoinsValue1M ?? null);
          break;
        case TIMESPAN.week:
          setPerformance(data.evolutionPeriode.performance1W ?? null);
          setPlusMoinsValue(data.evolutionPeriode.plusMoinsValue1W ?? null);
          break;
        default:
          break;
      }
      return;
    }

    // Fallback to evolution if evolutionPeriode is not available
    setPerformance(data.evolution?.performance ?? null);
    setPlusMoinsValue(data.evolution?.plusMoinsValue ?? null);
  };

  const filterPerformanceInvestment = ({
    lineChartTimespan,
    setPerformance,
    setPlusMoinsValue,
  }: PerformanceFilterProps) => {
    if (!data?.evolutionPeriode || !Array.isArray(data.evolutionPeriode)) return;

    switch (lineChartTimespan) {
      case TIMESPAN.allTime:
        setPerformance(data.evolutionPeriode?.[0]?.performance ?? null);
        setPlusMoinsValue(data.evolutionPeriode?.[0]?.plusMoinsValue ?? null);
        break;
      case TIMESPAN.year:
        setPerformance(data.evolutionPeriode?.[1]?.performance ?? null);
        setPlusMoinsValue(data.evolutionPeriode?.[1]?.plusMoinsValue ?? null);
        break;
      case TIMESPAN.month:
        setPerformance(data.evolutionPeriode?.[3]?.performance ?? null);
        setPlusMoinsValue(data.evolutionPeriode?.[3]?.plusMoinsValue ?? null);
        break;
      case TIMESPAN.week:
        setPerformance(data.evolutionPeriode?.[4]?.performance ?? null);
        setPlusMoinsValue(data.evolutionPeriode?.[4]?.plusMoinsValue ?? null);
        break;
      default:
        break;
    }
  };

  return { filterPerformanceDashboard, filterPerformanceInvestment };
};
